import { fork, call, put, takeEvery } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import moment from 'moment';
import config from 'config';
import * as http from 'utils/http';
import serialize from 'utils/serialize';

import { advanceJourney as advanceJourneyRequest, fetchJourney } from 'redux/modules/journeys/http';

import { sendEventToSegment } from 'redux/modules/analytics';

import { ADVANCE_JOURNEY_STATUS, GET_ETA_ON_DEMAND } from 'redux/modules/analytics/events';

import { createJourneyLog } from 'redux/modules/log/effects';

import { logChangesGenerator } from 'redux/modules/log/log-changes';

import {
  setAdvancing,
  setNotAdvancing,
  setETAAccuracyError,
  setETAData,
  setOpened,
  setNotOpened,
} from './duck';

import { displayError } from 'redux/modules/error-handler/effects';
import { JOURNEY_ADVANCE } from 'redux/modules/error-handler/constants';
import { computeJourney } from 'redux/modules/job-detail/effects';
import { setData } from 'redux/modules/job-detail/duck';
import { GIS_HEADERS } from 'tenant/bussines';

const advanceJourney$ = 'journey-stop/advanceJourney$';
const fetchETA$ = 'journey-stop/fetchETA$';

const setStageOpened$ = 'journey-stop/setStageOpened$';
const setStageClosed$ = 'journey-stop/setStageClosed$';

export const advanceJourney = (payload) => ({
  type: advanceJourney$,
  payload,
});

export const fetchETA = (payload) => ({
  type: fetchETA$,
  payload,
});

export const setStageOpened = (payload) => ({
  type: setStageOpened$,
  payload,
});

export const setStageClosed = (payload) => ({
  type: setStageClosed$,
  payload,
});

function* handleETARemoving({ journeyId }) {
  yield call(delay, 60000);
  yield put(setETAData(journeyId, null));
}

function* handleJourneyAdvance({ payload }) {
  const { actionSource, journeyId, stage, stopId, companyName, stopType, time } = payload;
  try {
    yield call(sendEventToSegment, ADVANCE_JOURNEY_STATUS, {
      journeyId,
      actionSource,
      stopKey: stopId,
      selectedTime: !!time ? time : null,
    });

    yield put(setAdvancing(journeyId, { stopId, stage }));
    yield call(advanceJourneyRequest, { journeyId, stop_id: stopId, stop_time: time });

    const journey = yield call(fetchJourney, journeyId);
    const computedJourney = yield call(computeJourney, journey);
    yield put(setData(computedJourney));

    const action = 'CHANGE_JOURNEY_STATUS';

    const changes = logChangesGenerator.get(action, {
      stage,
      stop_type: stopType,
      stop_company_name: companyName,
    });

    yield put(
      createJourneyLog({
        action,
        journeyId,
        changes,
      })
    );
  } catch (e) {
    yield put(displayError(e, JOURNEY_ADVANCE));
    console.log(e);
  } finally {
    yield put(setNotAdvancing(journeyId));
  }
}

const VALID_ACCURACIES = ['HIGH', 'MEDIUM'];

function* handleFetchETA({ payload }) {
  const { actionSource, journeyId, driver, address } = payload;
  try {
    const { data } = yield call(
      http.get,
      `${config.GEO_URL}gps/eta/${driver}?${serialize(address)}`,
      null,
      GIS_HEADERS
    );

    if (!data) {
      yield put(setETAAccuracyError(journeyId, 'driver_position_not_accurate'));
      yield call(sendEventToSegment, GET_ETA_ON_DEMAND, {
        journeyId,
        actionSource,
        error: 'driver_position_not_accurate',
      });
      return;
    }

    const { address: responseAddress, travel_time, distance } = data;
    const { accuracy_level } = responseAddress;

    if (!VALID_ACCURACIES.includes(accuracy_level)) {
      yield put(setETAAccuracyError(journeyId, 'destination_position_not_accurate'));
      yield call(sendEventToSegment, GET_ETA_ON_DEMAND, {
        journeyId,
        actionSource,
        error: 'destination_position_not_accurate',
      });
      return;
    }

    const retrieved_time = moment().valueOf();

    yield put(setETAData(journeyId, { travel_time, distance, retrieved_time }));
    yield put(setNotOpened(journeyId));

    yield call(sendEventToSegment, GET_ETA_ON_DEMAND, {
      journeyId,
      actionSource,
      error: null,
      travel_time,
      retrieved_time,
    });

    yield fork(handleETARemoving, { journeyId });
  } catch (err) {
    yield put(setETAAccuracyError(journeyId, 'driver_position_not_accurate'));
    yield call(sendEventToSegment, GET_ETA_ON_DEMAND, {
      journeyId,
      actionSource,
      error: 'driver_position_not_accurate',
    });
  }
}

function* handleOpenStage({ payload }) {
  const { journeyId, stopId, stage } = payload;
  yield put(setOpened(journeyId, { stopId, stage }));
}

function* handleCloseStage({ payload }) {
  const { journeyId } = payload;
  yield put(setNotOpened(journeyId));
  yield put(setETAAccuracyError(journeyId, false));
}

// Watchers
export function* watchJourneyStops() {
  yield takeEvery(advanceJourney$, handleJourneyAdvance);
  yield takeEvery(fetchETA$, handleFetchETA);

  yield takeEvery(setStageOpened$, handleOpenStage);
  yield takeEvery(setStageClosed$, handleCloseStage);
}
